import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarketingHeader from '../../components/MarketingHeader';
import {
  useCreateSocialPost,
  useReplaceSocialPostTargets,
  useScheduleSocialPost,
  useSocialAccounts,
  useSocialPost,
  useSocialPostTargets,
  useSubmitSocialPostForApproval,
  useUpdateSocialPost,
} from '../../hooks/useSocial';
import { PrimaryButton, SecondaryButton } from '../../styles/marketingSharedStyles';
import { Card, CardTitle, Input, Item, List, Page, Row, Subtle, TextArea } from './socialStyles';

const SocialComposerPage: React.FC = () => {
  const params = useParams<{ postId?: string }>();
  const postId = params.postId;
  const navigate = useNavigate();
  const post = useSocialPost(postId);
  const targets = useSocialPostTargets(postId);
  const accounts = useSocialAccounts();
  const createPost = useCreateSocialPost();
  const updatePost = useUpdateSocialPost();
  const replaceTargets = useReplaceSocialPostTargets();
  const submitForApproval = useSubmitSocialPostForApproval();
  const schedulePost = useScheduleSocialPost();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [savingError, setSavingError] = useState<string | null>(null);

  useEffect(() => {
    if (!post.data) return;
    setTitle(post.data.title ?? '');
    setBody(post.data.body ?? '');
    setScheduledFor(post.data.scheduled_for ? post.data.scheduled_for.slice(0, 16) : '');
    setImageUrl(typeof post.data.metadata?.image_url === 'string' ? String(post.data.metadata.image_url) : '');
    setYoutubeVideoId(
      typeof post.data.metadata?.youtube_video_id === 'string' ? String(post.data.metadata.youtube_video_id) : ''
    );
  }, [post.data]);

  useEffect(() => {
    if (!targets.data) return;
    setSelectedAccountIds(targets.data.map((target) => target.account_id));
  }, [targets.data]);

  const groupedAccounts = useMemo(() => accounts.data ?? [], [accounts.data]);

  const toggleTarget = (accountId: string) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  };

  const saveDraft = async () => {
    try {
      setSavingError(null);
      const payload = {
        title,
        body,
        scheduledFor: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        metadata: {
          image_url: imageUrl || null,
          youtube_video_id: youtubeVideoId || null,
        },
      };
      if (!postId) {
        const created = await createPost.mutateAsync({
          ...payload,
          workflowStatus: 'draft',
          targetAccountIds: selectedAccountIds,
        });
        navigate(`/marketing/social/composer/${created.id}`, { replace: true });
      } else {
        await updatePost.mutateAsync({
          id: postId,
          changes: {
            title: payload.title,
            body: payload.body,
            scheduled_for: payload.scheduledFor,
            metadata: payload.metadata,
          },
        });
        await replaceTargets.mutateAsync({ postId, accountIds: selectedAccountIds });
      }
    } catch (error) {
      setSavingError(error instanceof Error ? error.message : 'Unable to save social draft.');
    }
  };

  const submit = async () => {
    if (!postId) {
      await saveDraft();
    }
    const targetId = postId || '';
    if (!targetId) return;
    await submitForApproval.mutateAsync(targetId);
  };

  const schedule = async () => {
    const targetId = postId || '';
    if (!targetId || !scheduledFor) return;
    await schedulePost.mutateAsync({
      postId: targetId,
      scheduledFor: new Date(scheduledFor).toISOString(),
    });
  };

  const busy =
    createPost.isPending ||
    updatePost.isPending ||
    replaceTargets.isPending ||
    submitForApproval.isPending ||
    schedulePost.isPending;

  return (
    <Page>
      <MarketingHeader activeTab="social" />

      <Card>
        <CardTitle>{postId ? 'Edit Social Post' : 'Create Social Post'}</CardTitle>
        <Row>
          <Input placeholder="Post title" value={title} onChange={(event) => setTitle(event.target.value)} />
          <Input
            type="datetime-local"
            value={scheduledFor}
            onChange={(event) => setScheduledFor(event.target.value)}
            placeholder="Schedule time"
          />
        </Row>
        <Row>
          <Input
            placeholder="Instagram image URL (required for IG publish)"
            value={imageUrl}
            onChange={(event) => setImageUrl(event.target.value)}
          />
          <Input
            placeholder="YouTube video ID (for YouTube workflow)"
            value={youtubeVideoId}
            onChange={(event) => setYoutubeVideoId(event.target.value)}
          />
        </Row>
        <TextArea
          placeholder="Write your social content..."
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
        {savingError ? <Subtle>{savingError}</Subtle> : null}
      </Card>

      <Card>
        <CardTitle>Select Target Accounts</CardTitle>
        <List>
          {groupedAccounts.map((account) => (
            <Item key={account.id}>
              <Row>
                <input
                  type="checkbox"
                  checked={selectedAccountIds.includes(account.id)}
                  onChange={() => toggleTarget(account.id)}
                />
                <strong>{account.display_name}</strong>
                <span>{account.platform}</span>
                <span>{account.status}</span>
              </Row>
            </Item>
          ))}
          {groupedAccounts.length === 0 ? <Subtle>Connect at least one account first.</Subtle> : null}
        </List>
      </Card>

      <Row>
        <PrimaryButton type="button" onClick={saveDraft} disabled={busy}>
          Save Draft
        </PrimaryButton>
        <SecondaryButton type="button" onClick={submit} disabled={busy || !postId}>
          Submit for Approval
        </SecondaryButton>
        <SecondaryButton type="button" onClick={schedule} disabled={busy || !postId || !scheduledFor}>
          Schedule
        </SecondaryButton>
      </Row>
    </Page>
  );
};

export default SocialComposerPage;
